<template>
    <div class="image-cropper-box" :style="{ minHeight: boxMinHeight }">
        <template v-if="display">
            <div v-if="placeholder" class="tw-simple-hover tw-flex tw-justify-center tw-w-full"
                :style="{ minHeight: boxMinHeight }" @click="startUpload">
                <img :src="placeholder" v-tooltip='{content: `<i class="fal fa-arrows-h"></i> ${ width } px <i class="fal fa-arrows-v tw-pl-2 tw-pr-1"></i>`}'>
            </div>
            <template v-else>
                <div class="empty-image-box tw-flex tw-flex-col tw-items-center tw-justify-center" @click="startUpload"
                    :class="{'with-default-height': display_empty_upload_text}">
                    <template v-if="display_empty_upload_text">
                        <i class="fa fa-upload tw-text-gray-500 tw-text-2xl tw-pb-3"></i>
                        <p class="tw-text-lg">Select image</p>
                    </template>
                    <template v-else>
                        <slot name="upload-trigger"></slot>
                    </template>
                </div>
            </template>
        </template>
        <div class="cropper-lightbox" :class="{'hidden': !lightbox_open}" ref="imageContainer">
            <div class="tw-flex tw-flex-col tw-items-center" v-click-outside="vcoConfig">
                <div class="cropper-lightbox-image-wrapper"
                    v-tooltip='{content: `<i class="fal fa-arrows-h"></i> ${ width } px <i class="fal fa-arrows-v tw-pl-2 tw-pr-1"></i> ${ height }px`, show: lightbox_open, autoHide: false}'>
                    <div class="mobile-overlay-wrapper" v-if="mobile_overlay"
                        :style="{height: mobile_overlay.height + 'px', width: mobile_overlay.width + 'px'}">
                        <div class="mobile-overlay tw-relative"
                            :style="{height: mobile_overlay.mobile_height + 'px', width: mobile_overlay.mobile_width + 'px'}">
                            <div
                                class="tw-absolute tw-right-0 tw-top-0 tw-flex tw-items-center tw-text-white tw-pr-4 tw-pt-4 tw-text-sm tw-font-semibold tw-opacity-75">
                                <i class="far fa-mobile-alt tw-pr-2" v-if="!custom_overlay_text"></i>
                                <p v-text="!custom_overlay_text ? $t('image.mobile_view') : custom_overlay_text"></p>
                            </div>
                        </div>
                    </div>
                    <image-cropper ref="image" accept="image/*" @file-choose="openLightbox" :show-remove-button="false"
                        :width="display_width" :height="display_height" :initial-image="placeholder"
                        :quality="display_ratio" @zoom="adjustZoomSliderVal"></image-cropper>
                    <!-- :prevent-white-space="true" -->
                </div>
                <div class="tw-flex tw-items-center tw-mt-4">
                    <clickable-icon bg_color="slot" color="tw-text-white" @click.native="closeLightbox">
                        <i class="fal fa-times tw-text-xl"></i>
                    </clickable-icon>
                    <div class="tw-flex tw-items-center tw-text-white tw-p-2 tw-rounded tw-mx-3 tw-text-sm"
                        style="background: rgba(0, 0, 0, 0.55);">
                        <i class="fal fa-draw-square tw-pr-2"></i>
                        <span class="tw-pr-5">Pinch/scroll to zoom</span>
                        <i class="fal fa-hand-point-left tw-pr-2" aria-hidden="true"></i>
                        <span>Drag to move</span>
                    </div>
                    <clickable-icon bg_color="slot" color="tw-text-white" @click.native="saveImage">
                        <i class="fal fa-check tw-text-xl"></i>
                    </clickable-icon>
                </div>
                <input class="zoom-slider tw-bg-none tw-mt-2 tw-hidden md:tw-block" type="range" @input="zoomChange"
                    :min="slider_min" :max="slider_max" step=".001" v-model="slider_val">
            </div>
        </div>
    </div>
</template>

<script>
import { EventBus } from '../../lib/event-bus';
import vClickOutside from 'v-click-outside';

export default {
    name: 'ImageUploader',

    props: {
        placeholder: {
            required: false,
            default: '',
        },

        extension: {
            default: false,
        },

        name: {
            default: false,
        },

        return_file: {
            default: false,
        },

        width: {
            required: true,
        },

        height: {
            required: true,
        },

        mobile_width: {
            default: false,
        },

        mobile_height: {
            default: false,
        },

        custom_overlay_text: {
            default: false,
        },

        display_empty_upload_text: {
            default: true,
        },

        min_height: {
            default: false,
        },

        // whether to display a preview of the image (if: true) (or a select box if there is no uploader) or to immediately init on mount (if: false)
        display: {
            default: true,
        },
    },

    data() {
        return {
            placeholder_removed: false,
            lightbox_open: false,
            vcoConfig: {
                handler: this.closeLightbox,
                events: ['mousedown'],
            },
            display_width: 1,
            display_height: 1,
            display_ratio: 1,
            file_name: '',
            file_type: '',
            mobile_overlay: false,
            slider_val: 0,
            slider_min: 0,
            slider_max: 0,
        };
    },

    computed: {
        boxMinHeight() {
            if (this.min_height !== false) {
                return `${this.min_height}px`;
            }

            return 'auto';
        },
    },

    methods: {
        async saveImage() {
            if (this.return_file) {
              this.$emit('upload',{
                  base64: this.$refs.image.generateDataUrl(`image/${this.extension}`, 1),
                  filename: this.file_name,
                  blob: await this.$refs.image.promisedBlob('image/jpeg', 0.8)
              });
            } else if (this.extension) {
                this.$emit('upload', {
                    base64: this.$refs.image.generateDataUrl(`image/${this.extension}`, 1),
                    filename: this.file_name,
                });
            } else {
                this.$emit('upload', {
                    base64: this.$refs.image.generateDataUrl(this.file_type, 1),
                    filename: this.file_name,
                });
            }
            this.closeLightbox();
        },

        startUpload() {
            this.$refs.image.chooseFile();
        },

        openLightbox(file) {
            // save data for future use
            this.file_name = file.name;
            this.file_type = file.type;

            this.lightbox_open = true;

            this.$nextTick(() => {
                const available_width = (this.$refs.imageContainer.offsetWidth * 0.8);

                if (this.width > available_width) {
                    const scaled_down_by = this.width / available_width;

                    this.display_width = available_width;
                    this.display_height = this.height / scaled_down_by;
                    this.display_ratio = scaled_down_by;
                }

                // zoom in and out because otherwise the scaleRatio isn't correct due to the conditional resizing above
                this.$refs.image.zoomIn();
                this.$refs.image.zoomOut();

                // wait a bit for the component to recalculate its properties
                setTimeout(() => {
                    this.slider_val = this.$refs.image.scaleRatio;
                    this.slider_min = this.$refs.image.scaleRatio / 2;
                    this.slider_max = this.$refs.image.scaleRatio * 2;
                }, 250);

                if (this.mobile_width && this.mobile_height) {
                    this.positionMobileOverlay();
                }
            });
        },

        closeLightbox() {
            this.lightbox_open = false;
            this.$emit('close');
        },

        positionMobileOverlay() {
            const mobile_width_percentage = this.mobile_width / this.width;
            const mobile_height_percentage = this.mobile_height / this.height;

            setTimeout(() => {
                this.mobile_overlay = {
                    width: this.$refs.image.canvas.offsetWidth,
                    height: this.$refs.image.canvas.offsetHeight,
                    mobile_width: this.$refs.image.canvas.offsetWidth * mobile_width_percentage,
                    mobile_height: this.$refs.image.canvas.offsetHeight * mobile_height_percentage,
                };
            }, 0);
        },

        adjustZoomSliderVal() {
            this.slider_val = this.$refs.image.scaleRatio;
        },

        zoomChange(event) {
            this.$refs.image.scaleRatio = Number(event.target.value);
        },
    },

    mounted() {
        this.display_width = this.width;
        this.display_height = this.height;

        if (this.name) {
            EventBus.$on(`open-image-uploader-${this.name}`, () => {
                this.startUpload();
            });
        }

        if (!this.display) {
            this.startUpload();
        }

        // const canvas = this.$refs.image.getCanvas();

        // canvas.style.height = 'auto';
        // canvas.style.width = '100%';

        if (this.mobile_width && this.mobile_height) {
            window.addEventListener('resize', this.positionMobileOverlay);
        }
    },

    beforeDestroy() {
        if (this.name) {
            EventBus.$off(`open-image-uploader-${this.name}`);
        }

        if (this.mobile_width && this.mobile_height) {
            window.removeEventListener('resize', this.positionMobileOverlay);
        }
    },

    directives: {
        clickOutside: vClickOutside.directive
    },
};
</script>

<style lang="scss">
.image-cropper-box {
    @apply tw-flex tw-justify-center tw-rounded-lg tw-overflow-hidden tw-cursor-pointer tw-max-w-full tw-border tw-border-gray-500 tw-border-dashed;

    .empty-image-box {
        width: 100%;

        &.with-default-height {
            height: 175px;
        }
    }

    .cropper-lightbox {
        @apply tw-z-50 tw-w-screen tw-h-screen tw-fixed tw-left-0 tw-top-0 tw-flex tw-items-center tw-justify-center tw-cursor-auto;
        background: rgba(0, 0, 0, 0.5);

        .cropper-lightbox-image-wrapper {
            @apply tw-overflow-hidden; // rounded
            max-width: 80vw;
            max-height: 80vh;
            border: 2px dashed #929292;

            .mobile-overlay-wrapper {
                @apply tw-flex tw-absolute tw-items-center tw-justify-center tw-pointer-events-none;

                .mobile-overlay {
                    @apply tw-rounded;
                    border: 3px dashed purple;
                    background: rgba(128, 0, 128, 0.25);
                }
            }
        }

        .zoom-slider {
            width: 200px;

            &::-webkit-slider-runnable-track {
                background: none !important;
            }

            &::-moz-range-track {
                background: none !important;
            }

            &::-ms-track {
                background: none !important;
            }
        }

        &.hidden {
            display: none !important;
        }
    }

    .croppa-container {
        @apply tw-hidden;

        &.croppa--has-target {
            @apply tw-block tw-cursor-move;

            canvas {
                @apply tw-h-auto tw-max-w-full #{!important};
            }

            &.hidden {
                display: none !important;
            }
        }
    }

    &.has-placeholder {
        .croppa-container {
            &.croppa--has-target {
                @apply tw-cursor-pointer;
            }
        }
    }
}
</style>
